import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Shield, Users, Tag, Loader2, Save, X, Plus, AlertCircle, RefreshCw, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = (() => {
    const url = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
    return url.endsWith('/api') ? url : `${url}/api`;
})();

const AdminPanel = () => {
    const { user, token, logout } = useAuth();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'coupons'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Data
    const [users, setUsers] = useState([]);
    const [coupons, setCoupons] = useState([]);

    // Modals & Forms
    const [editingUser, setEditingUser] = useState(null);
    const [editBalance, setEditBalance] = useState('');
    const [couponForm, setCouponForm] = useState({ value: 100000, quantity: 1 });

    useEffect(() => {
        // Redirect if not admin
        if (user && !user.is_admin) {
            navigate('/editor');
        } else if (user && user.is_admin) {
            fetchData();
        }
    }, [user, activeTab]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const endpoint = activeTab === 'users' ? '/admin/users' : '/admin/coupons';
            const res = await fetch(`${API_BASE_URL}${endpoint}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Veri çekilemedi');

            if (activeTab === 'users') {
                setUsers(data.users || []);
            } else {
                setCoupons(data.coupons || []);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateBalance = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            const res = await fetch(`${API_BASE_URL}/admin/users/${editingUser._id}/balance`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ tokens_remaining: parseInt(editBalance, 10) })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setSuccess('Kullanıcı bakiyesi güncellendi');
            setEditingUser(null);
            fetchData(); // Refresh list
        } catch (err) {
            setError(err.message);
        }
    };

    const handleGenerateCoupons = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            const res = await fetch(`${API_BASE_URL}/admin/coupons`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    value: parseInt(couponForm.value, 10),
                    quantity: parseInt(couponForm.quantity, 10)
                })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setSuccess(data.message);
            setCouponForm({ value: 100000, quantity: 1 }); // Reset form
            fetchData(); // Refresh list
        } catch (err) {
            setError(err.message);
        }
    };

    if (!user || !user.is_admin) return null;

    return (
        <div className="min-h-screen bg-soft-blue flex flex-col font-sans">
            {/* Admin Header */}
            <header className="bg-slate-900 text-white shadow-xl sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-red-500 to-orange-500 p-2 rounded-xl shadow-lg">
                            <Shield className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight">CeviriPro Admin Paneli</h1>
                            <p className="text-xs text-slate-400 font-medium">Yetkili Erişim</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-slate-300 hidden sm:block">{user.email}</span>
                        <button onClick={() => navigate('/profile')} className="text-sm text-slate-300 hover:text-white transition-colors">
                            Profile Dön
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">

                {/* Navigation Tabs */}
                <div className="flex space-x-2 mb-8 bg-white p-1 rounded-2xl shadow-sm border border-slate-200 w-fit">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${activeTab === 'users' ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        <Users className="w-4 h-4" /> Kullanıcılar
                    </button>
                    <button
                        onClick={() => setActiveTab('coupons')}
                        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${activeTab === 'coupons' ? 'bg-indigo-50 text-indigo-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        <Tag className="w-4 h-4" /> Kod (Kupon) Üretimi
                    </button>
                </div>

                {/* Status Messages */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-red-800 font-medium">{error}</p>
                        <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:bg-red-100 p-1 rounded-lg">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {success && (
                    <div className="mb-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-xl flex items-start gap-3">
                        <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <p className="text-emerald-800 font-medium">{success}</p>
                        <button onClick={() => setSuccess(null)} className="ml-auto text-emerald-500 hover:bg-emerald-100 p-1 rounded-lg">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* CONTENT AREA */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
                    <div className="flex justify-between items-center p-6 border-b border-slate-100">
                        <h2 className="text-xl font-bold text-slate-800">
                            {activeTab === 'users' ? 'Kullanıcı Yönetimi' : 'Kupon Kodu Sistemi'}
                        </h2>
                        <button onClick={fetchData} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all" title="Yenile">
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>

                    <div className="p-6">
                        {loading && !users.length && !coupons.length ? (
                            <div className="flex justify-center p-12">
                                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                            </div>
                        ) : activeTab === 'users' ? (

                            /* USERS TAB */
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm whitespace-nowrap">
                                    <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-6 py-4 font-bold">Kullanıcı</th>
                                            <th className="px-6 py-4 font-bold">Kayıt Tarihi</th>
                                            <th className="px-6 py-4 font-bold text-right">Mevcut Bakiye</th>
                                            <th className="px-6 py-4 font-bold text-right">İşlem</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {users.map((u) => (
                                            <tr key={u._id} className="hover:bg-blue-50/50 transition-colors">
                                                <td className="px-6 py-4 flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                                                        {u.first_name?.[0]}{u.last_name?.[0]}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-900 flex items-center gap-2">
                                                            {u.first_name} {u.last_name}
                                                            {u.is_admin && <Shield className="w-3 h-3 text-red-500" title="Admin" />}
                                                        </div>
                                                        <div className="text-slate-500">{u.email}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    {u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '-'}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <span className="font-bold text-slate-800 text-lg">
                                                        {(u.tokens_remaining || 0).toLocaleString('tr-TR')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => { setEditingUser(u); setEditBalance(u.tokens_remaining || 0); }}
                                                        className="px-4 py-2 bg-slate-100 hover:bg-blue-100 text-blue-700 font-semibold rounded-lg transition-colors"
                                                    >
                                                        Bakiye Düzenle
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        ) : (

                            /* COUPONS TAB */
                            <div>
                                {/* Generate Form */}
                                <form onSubmit={handleGenerateCoupons} className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100 mb-8 flex flex-col md:flex-row gap-4 items-end">
                                    <div className="flex-1 w-full">
                                        <label className="block text-sm font-bold text-indigo-900 mb-2">Kupon Değeri</label>
                                        <select
                                            value={couponForm.value}
                                            onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })}
                                            className="w-full p-3 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 bg-white font-semibold text-slate-700"
                                        >
                                            <option value={100000}>100.000 Karakter (Starter)</option>
                                            <option value={500000}>500.000 Karakter (Pro)</option>
                                            <option value={1000000}>1.000.000 Karakter (Value)</option>
                                        </select>
                                    </div>
                                    <div className="w-full md:w-32">
                                        <label className="block text-sm font-bold text-indigo-900 mb-2">Adet</label>
                                        <input
                                            type="number"
                                            min="1" max="100"
                                            value={couponForm.quantity}
                                            onChange={(e) => setCouponForm({ ...couponForm, quantity: e.target.value })}
                                            className="w-full p-3 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 bg-white font-semibold text-slate-700"
                                        />
                                    </div>
                                    <button type="submit" className="w-full md:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                                        <Plus className="w-5 h-5" /> Üret
                                    </button>
                                </form>

                                {/* Coupons Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm whitespace-nowrap">
                                        <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-6 py-4 font-bold">Kod</th>
                                                <th className="px-6 py-4 font-bold">Değer</th>
                                                <th className="px-6 py-4 font-bold text-center">Durum</th>
                                                <th className="px-6 py-4 font-bold">Oluşturulma</th>
                                                <th className="px-6 py-4 font-bold">Kullanan / Tarih</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {coupons.map((c) => (
                                                <tr key={c._id} className={`transition-colors ${!c.is_active ? 'bg-slate-50/50' : 'hover:bg-indigo-50/30'}`}>
                                                    <td className="px-6 py-4 font-mono font-bold text-slate-800">
                                                        <span className={!c.is_active ? 'line-through text-slate-400' : ''}>
                                                            {c.code}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 font-bold text-indigo-600">
                                                        {(c.value).toLocaleString('tr-TR')}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        {c.is_active ? (
                                                            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-wide">Müsait</span>
                                                        ) : (
                                                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold uppercase tracking-wide">Kullanıldı</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-500 text-xs">
                                                        {new Date(c.created_at).toLocaleString('tr-TR')}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {!c.is_active ? (
                                                            <div>
                                                                <div className="font-mono text-xs text-slate-400 break-all w-32 truncate" title={c.used_by}>
                                                                    UID: {c.used_by}
                                                                </div>
                                                                <div className="text-xs text-red-500 font-medium">
                                                                    {new Date(c.used_at).toLocaleDateString('tr-TR')}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-300">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Edit Balance Modal */}
            {editingUser && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800">Bakiye Düzenle</h3>
                            <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:bg-slate-100 p-2 rounded-xl transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateBalance} className="p-6">
                            <div className="mb-6">
                                <p className="text-sm font-medium text-slate-500 mb-1">Kullanıcı</p>
                                <p className="font-bold text-slate-900">{editingUser.first_name} {editingUser.last_name}</p>
                                <p className="text-xs text-slate-400 break-all">{editingUser.email}</p>
                            </div>

                            <div className="mb-8">
                                <label className="block text-sm font-bold text-slate-700 mb-2">Yeni Karakter Bakiyesi</label>
                                <input
                                    type="number"
                                    value={editBalance}
                                    onChange={(e) => setEditBalance(e.target.value)}
                                    className="w-full p-4 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-0 text-lg font-bold text-slate-800"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button type="button" onClick={() => setEditingUser(null)} className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl transition-colors">
                                    İptal
                                </button>
                                <button type="submit" className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2">
                                    <Save className="w-5 h-5" /> Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
